import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = {
  key: 0,
  class: "mt-4 flex bg-gray-50 px-4 py-4 shadow ring-1 ring-gray-300 sm:p-6 md:rounded"
}
const _hoisted_4 = { class: "text-center text-xs font-medium text-gray-500" }
const _hoisted_5 = { class: "mt-1 text-center text-2xl font-semibold text-gray-800" }
const _hoisted_6 = {
  key: 2,
  class: "mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2"
}

import { ref, Ref, computed, watch, ComputedRef, onMounted } from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import { toastServiceError } from '@/services/core/notification';
import { ContactCenterPerDayReportService } from '@/services';

import {
    BarChartConfig,
    CallsStatsPerDay,
    CallType,
    ContactCenterPerDayReportActions,
    ContactCenterPerDayReportFilter,
    ContactCenterPerDayReportGetters,
    ContactCenterPerDayReportServiceGetInfoParams,
    ContactCenterPerDayReportServiceGetInfoResult,
    DataItem,
    FilterItem,
    LevelModel,
} from '@/types';

import { ContactCenterPerDayReportFilters } from '@/views/pages/reports/contact-center-per-day/components/';

// Constants
const chartColorBlue = '#BFDBFE';
const chartColorCyan = '#A5F3FC';

// State

export default /*@__PURE__*/_defineComponent({
  __name: 'ContactCenterPerDayReportPage',
  setup(__props) {

const reportInfo: Ref<ContactCenterPerDayReportServiceGetInfoResult | null> = ref(null);

// Computed
const headerStats = computed<DataItem<string>[]>(() => {
    const stats = reportInfo.value?.stats;
    if (!stats) return [];

    return [
        { name: 'totalCalls', value: stats.total.toString() },
        { name: 'inboundCalls', value: stats.type[CallType.INBOUND].toString() },
        { name: 'outboundCalls', value: stats.type[CallType.OUTBOUND].toString() },
        { name: 'internalCalls', value: stats.type[CallType.INTERNAL].toString() },
        { name: 'uniqueNumbers', value: stats.uniqueNumbers.toString() },
        {
            name: 'averageServiceTime',
            value: moment.utc((stats.averageServiceTime || 0) * 1000).format('HH:mm:ss'),
        },
    ];
});

const charts = computed<BarChartConfig[]>(() => [
    getChart(
        t('report.contactCenterPerDay.serviceTimePerDayColumnChart.title'),
        'averageServiceTime',
        chartColorBlue,
        yAxisLabelFormatterAsTime,
    ),
    getChart(
        t('report.contactCenterPerDay.durationPerDayColumnChart.title'),
        'averageDuration',
        chartColorCyan,
        yAxisLabelFormatterAsTime,
    ),
]);

// Composables
const uiStore = useUiStore();
const filterStore = useFilterStore();
const { t } = useI18n();

const levelFilter = computed<LevelModel | null>(() => filterStore.level);
const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (value) => filterStore.setDateRange(value),
});
const filter = computed<ContactCenterPerDayReportFilter>(
    () => store.getters[ContactCenterPerDayReportGetters.FILTER],
);
const activeFilters: ComputedRef<FilterItem[]> = computed(
    () => store.getters[ContactCenterPerDayReportGetters.ACTIVE_FILTERS],
);

// Watchers
watch([levelFilter, dateRange, activeFilters], () => {
    fetchReportData();
});

// Methods
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchReportData = async () => {
    const params: ContactCenterPerDayReportServiceGetInfoParams = {
        startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
        endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
        type: filter.value.type?.value || null,
        status: filter.value.status?.value || null,
        user: filter.value.user?.id || null,
        number: filter.value.number?.number || null,
        serviceTimeRange: filter.value.serviceTimeRange?.value || null,
        userLabel: filter.value.userLabel?.id || null,
    };

    try {
        setLoading(true);
        const { data } = await ContactCenterPerDayReportService.getInfo({ params });
        reportInfo.value = data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleRemoveFilter = (filter: FilterItem) => {
    store.dispatch(ContactCenterPerDayReportActions.CLEAR_FILTER, filter.field);
};

const getChart = (
    title: string,
    prop: keyof CallsStatsPerDay,
    color = chartColorBlue,
    yAxisLabelFormatter = (value: number | string) => String(value),
): BarChartConfig => {
    const stats = [...(reportInfo.value?.callsStatsPerDay || [])].reverse();

    const category = stats.map((s) => s.date);
    const value = stats.map((s) => s[prop] || 0);

    return {
        title,
        category,
        series: [
            {
                name: title,
                value,
                color,
            },
        ],
        yAxisLabelFormatter,
    };
};

const yAxisLabelFormatterAsTime = (value: number | string) =>
    moment.utc((Number(value) || 0) * 1000).format('HH:mm:ss');

const xAxisLabelFormatterAsDate = (value: number | string) => moment.utc(value).format('DD/MM');

// Lifecycle
onMounted(() => {
    fetchReportData();
});

return (_ctx: any,_cache: any) => {
  const _component_UiDatePicker = _resolveComponent("UiDatePicker")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_CallTypePerDayColumnChart = _resolveComponent("CallTypePerDayColumnChart")!
  const _component_CallStatusPerDayColumnChart = _resolveComponent("CallStatusPerDayColumnChart")!
  const _component_BarChart = _resolveComponent("BarChart")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('report.contactCenterPerDay.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createVNode(_unref(ContactCenterPerDayReportFilters)),
        _createVNode(_component_UiDatePicker, {
          modelValue: dateRange.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRange).value = $event)),
          range: "",
          multiCalendars: "",
          class: "ml-4"
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }),
    (activeFilters.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter) => {
            return (_openBlock(), _createBlock(_component_UiBadge, {
              key: filter,
              removable: "",
              onRemove: ($event: any) => (handleRemoveFilter(filter))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t(`report.contactCenterPerDay.filters.activeTag.${filter.field}`)) + ": " + _toDisplayString(filter.label), 1)
              ]),
              _: 2
            }, 1032, ["onRemove"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (headerStats.value)
      ? (_openBlock(), _createElementBlock("dl", {
          key: 1,
          class: _normalizeClass(["mt-4 grid grid-cols-2 gap-4", ['sm:grid-cols-' + headerStats.value.length]])
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerStats.value, (stat) => {
            return (_openBlock(), _createBlock(_component_UiPanel, {
              key: stat.name
            }, {
              default: _withCtx(() => [
                _createElementVNode("dt", _hoisted_4, _toDisplayString(_ctx.$t('report.contactCenterPerDay.stats.' + stat.name)), 1),
                _createElementVNode("dd", _hoisted_5, _toDisplayString(stat.value), 1)
              ]),
              _: 2
            }, 1024))
          }), 128))
        ], 2))
      : _createCommentVNode("", true),
    (reportInfo.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(_component_CallTypePerDayColumnChart, {
            title: _unref(t)('report.contactCenterPerDay.callTypePerDayColumnChart.title'),
            data: reportInfo.value.callsStatsPerDay,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallStatusPerDayColumnChart, {
            title: _unref(t)('report.contactCenterPerDay.callStatusPerDayColumnChart.title'),
            data: reportInfo.value.callsStatsPerDay,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(charts.value, (chart, index) => {
            return (_openBlock(), _createBlock(_component_BarChart, {
              key: index,
              title: chart.title,
              categoryData: chart.category,
              seriesData: chart.series,
              xAxisLabelFormatter: xAxisLabelFormatterAsDate,
              yAxisLabelFormatter: chart.yAxisLabelFormatter,
              class: "col-span-2 h-[300px] md:col-span-1",
              column: ""
            }, null, 8, ["title", "categoryData", "seriesData", "yAxisLabelFormatter"]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})